<template>
    <div class="cat-page">
        <div class="card">
            <div class="card-header cat-header">
                <div class="cat-header-titles">
                    <span class="cat-title">{{ $t('pages.stock.category.title') }}</span>
                    <span class="cat-subtitle">{{ $t('pages.stock.category.subTitle') }}</span>
                </div>
                <div class="cat-toolbar">
                    <el-tooltip :content="$t('pages.stock.category.expandAll')" placement="top">
                        <button type="button" class="cat-tool-btn" @click="expandAll" :disabled="!hasData">
                            <i class="bi bi-arrows-expand"></i>
                        </button>
                    </el-tooltip>
                    <el-tooltip :content="$t('pages.stock.category.collapseAll')" placement="top">
                        <button type="button" class="cat-tool-btn" @click="collapseAll" :disabled="!hasData">
                            <i class="bi bi-arrows-collapse"></i>
                        </button>
                    </el-tooltip>
                    <el-tooltip :content="$t('btn.refresh')" placement="top">
                        <button type="button" class="cat-tool-btn" @click="refreshTable">
                            <i class="bi bi-arrow-repeat"></i>
                        </button>
                    </el-tooltip>
                    <button type="button" class="btn btn-sm btn-primary cat-add-btn" @click="newCategory()">
                        <i class="bi bi-plus-lg me-1"></i>{{ $t('pages.stock.category.addRoot') }}
                    </button>
                </div>
            </div>

            <div class="card-body cat-body">
                <div class="cat-search">
                    <i class="bi bi-search cat-search-icon"></i>
                    <input
                        v-model="searchTerm"
                        type="text"
                        class="cat-search-input"
                        :placeholder="$t('pages.stock.category.searchPlaceholder')"
                    />
                    <button
                        v-if="searchTerm"
                        type="button"
                        class="cat-search-clear"
                        :title="$t('pages.stock.category.clearSearch')"
                        @click="searchTerm = ''"
                    >
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>

                <div class="cat-tree-wrap" :class="{ 'cat-tree-wrap--loading': loading }">
                    <div v-if="loading" class="cat-loading">
                        <span class="spinner-border text-primary" role="status"></span>
                    </div>

                    <template v-if="!loading && !hasData">
                        <div class="cat-empty">
                            <div class="cat-empty-icon"><i class="bi bi-diagram-3"></i></div>
                            <div class="cat-empty-title">{{ $t('pages.stock.category.emptyTitle') }}</div>
                            <div class="cat-empty-desc">{{ $t('pages.stock.category.emptyDesc') }}</div>
                            <button type="button" class="btn btn-sm btn-primary mt-4" @click="newCategory()">
                                <i class="bi bi-plus-lg me-1"></i>{{ $t('pages.stock.category.emptyCta') }}
                            </button>
                        </div>
                    </template>

                    <template v-else-if="!loading && hasData && !visibleRoots.length">
                        <div class="cat-empty">
                            <div class="cat-empty-icon"><i class="bi bi-search"></i></div>
                            <div class="cat-empty-desc">{{ noResultText }}</div>
                        </div>
                    </template>

                    <div v-else-if="!loading" class="cat-tree">
                        <CategoryTreeNode
                            v-for="root in visibleRoots"
                            :key="root.id"
                            :node="root"
                            :depth="0"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="kt_modal_category" ref="categoryModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered mw-600px">
                <div class="modal-content">
                    <div class="modal-header">
                        <h2 class="fw-bolder">{{ form.title }}</h2>
                        <div id="kt_modal_category_close" data-bs-dismiss="modal" class="btn btn-icon btn-sm btn-active-icon-primary">
                            <span class="svg-icon svg-icon-1">
                                <inline-svg src="/media/icons/duotune/arrows/arr061.svg"/>
                            </span>
                        </div>
                    </div>
                    <el-form @submit.prevent="onSubmit()" :model="form" ref="categoryForm">
                        <div class="modal-body py-8 px-lg-12">
                            <div v-if="!form.updateStatus" class="cat-ctx" :class="form.data.parent_id ? 'cat-ctx--sub' : 'cat-ctx--root'">
                                <i class="bi" :class="form.data.parent_id ? 'bi-diagram-2' : 'bi-folder2'"></i>
                                <span>{{ contextText }}</span>
                            </div>

                            <div class="fv-row mb-7">
                                <label class="required fs-6 fw-bold mb-2">{{ $t('pages.stock.category.nameLabel') }}</label>
                                <el-form-item prop="data.name" :rules="$validation.getMessage(['required'])">
                                    <el-input
                                        v-model="form.data.name"
                                        type="text"
                                        :placeholder="$t('pages.stock.category.namePlaceholder')"
                                    />
                                </el-form-item>
                            </div>

                            <div v-if="!form.data.parent_id" class="fv-row mb-2">
                                <label class="fs-6 fw-bold mb-2">
                                    {{ $t('pages.stock.category.cols.module') }}
                                    <el-popover :content="$t('pages.stock.category.module_description')" placement="top" :width="360">
                                        <template #reference>
                                            <i class="bi bi-info-circle cat-info"></i>
                                        </template>
                                    </el-popover>
                                </label>
                                <el-form-item>
                                    <el-select v-model="form.data.module_id" class="w-100" :placeholder="$t('common.chooseSelect')" filterable multiple>
                                        <el-option v-for="(item, itemIndex) in modules" :key="itemIndex" :value="item.id" :label="item.name"></el-option>
                                    </el-select>
                                </el-form-item>
                            </div>
                            <div v-else class="cat-subnotice">
                                <i class="bi bi-info-circle"></i>
                                <span>{{ $t('pages.stock.errors.module_only_for_root_category') }}</span>
                            </div>
                        </div>

                        <div class="modal-footer flex-center">
                            <button :data-kt-indicator="form.loading ? 'on' : null" class="btn btn-lg btn-primary" type="submit" :disabled="form.loading">
                                <span v-if="!form.loading" class="indicator-label">{{ $t("btn.save") }}</span>
                                <span v-if="form.loading" class="indicator-progress">
                                    {{ $t("messages.wait") }}
                                    <span class="spinner-border spinner-border-sm align-middle ms-2"></span>
                                </span>
                            </button>
                        </div>
                    </el-form>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CategoryTreeNode from "@/pages/stock/category/CategoryTreeNode.vue";

export default {
    name: "category",
    components: { CategoryTreeNode },
    data() {
        return {
            form: {},
            searchTerm: "",
            expandedMap: {},
            guardLoading: false,
            filterParameters: {}
        };
    },
    provide() {
        const self = this;
        return {
            catApi: {
                isExpanded: (id) => self.isExpanded(id),
                toggle: (id) => self.toggle(id),
                onEdit: (node) => self.fetchCategory(node),
                onAddChild: (node) => self.newCategory(node),
                onDelete: (node) => self.confirmDelete(node),
                descendantCount: (node) => self.countDescendants(node),
                filterChildren: (children) => self.filterChildren(children),
                get searchTerm() {
                    return self.searchTerm;
                }
            }
        };
    },
    computed: {
        table() {
            return this.$store.state.stock.category.table;
        },
        loading() {
            return this.table.loading && !this.guardLoading;
        },
        treeData() {
            return Array.isArray(this.table.data) ? this.table.data : [];
        },
        hasData() {
            return this.treeData.length > 0;
        },
        visibleRoots() {
            return this.filterChildren(this.treeData);
        },
        saveData() {
            return this.$store.state.stock.category.save.data;
        },
        modules() {
            return this.$store.state.company.module.table.data;
        },
        contextText() {
            if (this.form.data && this.form.data.parent_id) {
                return this.sprintf(this.$t("pages.stock.category.subContext"), [this.form.parentName ?? ""]);
            }
            return this.$t("pages.stock.category.rootContext");
        },
        noResultText() {
            return this.sprintf(this.$t("pages.stock.category.noSearchResultPattern"), [this.searchTerm]);
        }
    },
    created() {
        this.resetForm();
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("menu.category"), []);

        this.$store.dispatch("company/module/get", {
            page: { pageSize: 9999 }
        });

        this.refreshTable();
    },
    methods: {
        nodeMatches(node) {
            const term = this.searchTerm.trim().toLocaleLowerCase("tr-TR");
            if (!term) {
                return true;
            }
            return (node.name ?? "").toLocaleLowerCase("tr-TR").includes(term);
        },
        subtreeHasMatch(node) {
            if (this.nodeMatches(node)) {
                return true;
            }
            const children = Array.isArray(node.children) ? node.children : [];
            return children.some((child) => this.subtreeHasMatch(child));
        },
        filterChildren(children) {
            const list = Array.isArray(children) ? children : [];
            if (!this.searchTerm.trim()) {
                return list;
            }
            return list.filter((child) => this.subtreeHasMatch(child));
        },
        countDescendants(node) {
            const children = Array.isArray(node.children) ? node.children : [];
            let total = children.length;
            for (const child of children) {
                total += this.countDescendants(child);
            }
            return total;
        },
        isExpanded(id) {
            if (this.searchTerm.trim()) {
                return true;
            }
            return !!this.expandedMap[id];
        },
        toggle(id) {
            this.expandedMap = { ...this.expandedMap, [id]: !this.expandedMap[id] };
        },
        collectBranchIds(nodes, acc) {
            for (const node of nodes) {
                const children = Array.isArray(node.children) ? node.children : [];
                if (children.length) {
                    acc[node.id] = true;
                    this.collectBranchIds(children, acc);
                }
            }
            return acc;
        },
        expandAll() {
            this.expandedMap = this.collectBranchIds(this.treeData, {});
        },
        collapseAll() {
            this.expandedMap = {};
        },
        onSubmit() {
            this.$refs.categoryForm.validate((valid) => {
                if (!valid) {
                    return false;
                }

                this.form.loading = true;

                if (this.form.data.id) {
                    this.$store.dispatch("stock/category/update", {
                        id: this.form.data.id,
                        formData: this.form.data
                    }).then((response) => {
                        if (response?.status) {
                            this.onResponse(response, () => {
                                this.hideModal(this.$refs.categoryModal);
                                this.refreshTable();
                            });
                        }
                        this.form.loading = false;
                    });
                } else {
                    this.$store.dispatch("stock/category/add", {
                        formData: this.form.data
                    }).then((response) => {
                        if (response?.status) {
                            this.onResponse(response, () => {
                                this.hideModal(this.$refs.categoryModal);
                                this.refreshTable();
                            });
                        }
                        this.form.loading = false;
                    });
                }
            });
        },
        fetchCategory(record) {
            this.form.updateStatus = true;
            this.guardLoading = true;

            this.$store.dispatch("stock/category/show", {
                id: record.id
            }).then(() => {
                const data = this.saveData;
                data.module_id = data.modules?.map((module) => module.id) ?? [];

                this.form = {
                    title: this.sprintf(this.$t("pages.stock.category.editCategoryPattern"), [data.name ?? ""]),
                    loading: false,
                    updateStatus: true,
                    parentName: "",
                    data: data
                };

                this.showModal(this.$refs.categoryModal);
            }).finally(() => {
                this.guardLoading = false;
            });
        },
        confirmDelete(node) {
            const count = this.countDescendants(node);
            const message = count > 0
                ? this.sprintf(this.$t("pages.stock.category.deleteConfirmTree"), [node.name, count])
                : this.sprintf(this.$t("pages.stock.category.deleteConfirmLeaf"), [node.name]);

            this.eventBus.$emit("showConfirmDialog", {
                title: this.$t("common.delete"),
                message: message,
                confirmText: this.$t("common.delete"),
                confirmType: "danger",
                onConfirm: () => this.deleteRecord([node.id])
            });
        },
        newCategory(record = null) {
            this.resetForm();
            this.form.updateStatus = false;
            this.form.title = this.$t("pages.stock.category.newCategory");

            if (record) {
                this.form.title = this.sprintf(this.$t("pages.stock.category.newCategoryWithParent"), [record.name]);
                this.form.parentName = record.name;
                this.form.data.parent_id = record.id;
                this.form.data.module_id = null;
            }

            this.showModal(this.$refs.categoryModal);
        },
        deleteRecord(id) {
            this.$store.dispatch("stock/category/delete", {
                id: id
            }).then(() => {
                this.refreshTable();
            });
        },
        refreshTable() {
            this.$store.dispatch("stock/category/tree", {
                page: { pageSize: 9999 },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({}))
            });
        },
        resetForm() {
            this.form = {
                title: "",
                loading: false,
                updateStatus: false,
                parentName: "",
                data: {
                    name: null,
                    parent_id: null,
                    module_id: []
                }
            };
        }
    },
    watch: {
        treeData(list) {
            if (Array.isArray(list) && list.length) {
                this.expandAll();
            }
        }
    }
};
</script>

<style scoped>
.cat-page {
    --cat-accent: var(--panel-accent, #009ef7);
    --cat-fg: var(--bs-body-color, #181c32);
    --cat-muted: var(--bs-text-muted, #99a1b7);
    --cat-guide: var(--bs-border-color, #e4e6ef);
    --cat-row-hover: var(--bs-gray-100, #f9f9f9);
    --cat-caret-hover: var(--bs-gray-200, #f1f1f4);
    --cat-act-hover: var(--panel-selected-bg, rgba(0, 122, 255, 0.08));
    --cat-chip-bg: var(--bs-gray-100, #f1f1f4);
    --cat-chip-fg: var(--bs-gray-700, #5e6278);
    --cat-rootbadge-bg: var(--panel-selected-bg, rgba(0, 122, 255, 0.08));
    --cat-rootbadge-fg: var(--panel-accent-text, #009ef7);
    --cat-hl-bg: rgba(var(--panel-accent-rgb, 0, 122, 255), 0.22);
    --cat-hl-fg: var(--bs-body-color, #181c32);
    --cat-danger-bg: var(--bs-danger-light, rgba(241, 65, 108, 0.1));
    --cat-danger-fg: var(--bs-danger, #f1416c);
    --cat-field-bg: var(--bs-gray-100, #f4f6fa);
    --cat-field-border: var(--bs-border-color, #e4e6ef);
    --cat-field-border-focus: var(--panel-accent, #009ef7);
}

.cat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.cat-header-titles {
    display: flex;
    flex-direction: column;
}

.cat-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--cat-fg);
}

.cat-subtitle {
    font-size: 0.85rem;
    color: var(--cat-muted);
    margin-top: 2px;
}

.cat-toolbar {
    display: flex;
    align-items: center;
    gap: 6px;
}

.cat-tool-btn {
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--cat-field-border);
    border-radius: 8px;
    background-color: var(--cat-field-bg);
    color: var(--cat-fg);
    font-size: 14px;
    cursor: pointer;
    transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.cat-tool-btn i {
    display: block;
    line-height: 1;
}

.cat-tool-btn:hover:not(:disabled) {
    color: var(--cat-accent);
    border-color: var(--cat-accent);
    background-color: var(--cat-act-hover);
}

.cat-tool-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.cat-add-btn {
    margin-left: 4px;
    display: inline-flex;
    align-items: center;
}

.cat-body {
    padding-top: 22px;
}

.cat-search {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.cat-search-icon {
    position: absolute;
    left: 14px;
    color: var(--cat-muted);
    font-size: 14px;
    pointer-events: none;
}

.cat-search-input {
    width: 100%;
    height: 42px;
    padding: 0 40px 0 38px;
    border: 1px solid var(--cat-field-border);
    border-radius: 9px;
    background-color: var(--cat-field-bg);
    color: var(--cat-fg);
    font-size: 13.5px;
    outline: none;
    transition: border-color 0.12s ease, background-color 0.12s ease;
}

.cat-search-input::placeholder {
    color: var(--cat-muted);
}

.cat-search-input:focus {
    border-color: var(--cat-field-border-focus);
}

.cat-search-clear {
    position: absolute;
    right: 10px;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 5px;
    background-color: transparent;
    color: var(--cat-muted);
    font-size: 11px;
    cursor: pointer;
}

.cat-search-clear:hover {
    color: var(--cat-fg);
    background-color: var(--cat-caret-hover);
}

.cat-tree-wrap {
    position: relative;
    min-height: 220px;
}

.cat-tree {
    padding: 2px 0;
}

.cat-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cat-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 56px 20px;
}

.cat-empty-icon {
    width: 64px;
    height: 64px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    background-color: var(--cat-chip-bg);
    color: var(--cat-accent);
    font-size: 28px;
    margin-bottom: 16px;
}

.cat-empty-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--cat-fg);
    margin-bottom: 6px;
}

.cat-empty-desc {
    font-size: 0.88rem;
    color: var(--cat-muted);
    max-width: 320px;
}

.cat-ctx {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 12px;
    border-radius: 8px;
    font-size: 12.5px;
    font-weight: 600;
    margin-bottom: 18px;
}

.cat-ctx--root {
    background-color: var(--cat-rootbadge-bg);
    color: var(--cat-rootbadge-fg);
}

.cat-ctx--sub {
    background-color: var(--cat-chip-bg);
    color: var(--cat-chip-fg);
}

.cat-info {
    color: var(--cat-muted);
    cursor: pointer;
    margin-left: 4px;
}

.cat-subnotice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    background-color: var(--cat-chip-bg);
    color: var(--cat-chip-fg);
    font-size: 12px;
    line-height: 1.5;
}
</style>
